import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Card from "./ui/Card";

const EVENT_COLORS = [
  "#e11d48", "#7c3aed", "#0284c7", "#059669", "#d97706",
  "#db2777", "#0891b2", "#15803d", "#dc2626", "#6d28d9",
];

function PriceTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload?.date;
  return (
    <div className="bg-[var(--card)] border border-[var(--border)] rounded px-3 py-2 text-xs shadow-lg">
      <p className="text-[var(--muted)] mb-1">{d}</p>
      <p className="font-semibold">USD {Number(payload[0]?.value).toFixed(2)} / bbl</p>
    </div>
  );
}

// Custom label rendered from the bottom of each reference line
function EventLabel({ viewBox, name, color }) {
  const { x = 0, y = 0, height = 0 } = viewBox || {};
  const bottomY = y + height - 6;
  return (
    <g transform={`translate(${x},${bottomY}) rotate(-90)`}>
      <text
        x={0} y={0}
        textAnchor="start"
        fontSize={9}
        fill={color}
        style={{ pointerEvents: "none", userSelect: "none" }}
      >
        {name}
      </text>
    </g>
  );
}

export default function OilHistoryChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get("/oil/oil-history")
      .then((r) => { if (r.data?.data) setData(r.data.data); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Card><p className="text-sm text-[var(--muted)] animate-pulse">Carregando histórico de preços…</p></Card>
  );
  if (error) return (
    <Card><p className="text-sm text-red-500">Erro: {error}</p></Card>
  );
  if (!data?.prices?.length) return (
    <Card><p className="text-sm text-[var(--muted)]">Dados de preço indisponíveis.</p></Card>
  );

  const { prices, events } = data;

  // Use numeric timestamps so ReferenceLine x values always render (not constrained to data points)
  const chartData = prices.map((p) => ({
    date: p.date,
    ts: new Date(p.date).getTime(),
    price: p.value,
  }));

  const tsMin = chartData[0]?.ts;
  const tsMax = chartData[chartData.length - 1]?.ts;

  // Build evenly-spaced year ticks for the x-axis
  const yearTicks = [];
  const startYear = new Date(tsMin).getFullYear();
  const endYear = new Date(tsMax).getFullYear();
  for (let y = Math.ceil(startYear / 10) * 10; y <= endYear; y += 10) {
    yearTicks.push(new Date(`${y}-01-01`).getTime());
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold">Preço do Petróleo WTI — Histórico</h2>
        <p className="text-xs text-[var(--muted)] mt-0.5">
          Eventos geopolíticos marcados no gráfico · Fonte: FRED (mensal, desde 1946)
        </p>
      </div>

      <Card className="p-4">
        <ResponsiveContainer width="100%" height={380}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <defs>
              {events.map((ev, i) => {
                const color = EVENT_COLORS[i % EVENT_COLORS.length];
                return (
                  <pattern
                    key={`event-hatch-${ev.date}`}
                    id={`event-hatch-${ev.date}`}
                    patternUnits="userSpaceOnUse"
                    width="8"
                    height="8"
                    patternTransform="rotate(45)"
                  >
                    <line x1="0" y1="0" x2="0" y2="8" stroke={color} strokeWidth="1" opacity="0.35" />
                  </pattern>
                );
              })}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={[tsMin, tsMax]}
              ticks={yearTicks}
              tickFormatter={(ts) => new Date(ts).getFullYear()}
              tick={{ fontSize: 11, fill: "var(--muted)" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "var(--muted)" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `$${v}`}
              width={45}
            />
            <Tooltip content={<PriceTooltip />} />

            {events.map((ev, i) => {
              const color = EVENT_COLORS[i % EVENT_COLORS.length];
              const startTs = new Date(ev.startDate || ev.date).getTime();
              const endTs = new Date(ev.endDate || ev.date).getTime();
              const lineTs = new Date(ev.date).getTime();
              return (
                <React.Fragment key={ev.date}>
                  <ReferenceArea
                    x1={Math.min(startTs, endTs)}
                    x2={Math.max(startTs, endTs)}
                    fill={`url(#event-hatch-${ev.date})`}
                    fillOpacity={1}
                    stroke="none"
                  />
                  <ReferenceLine
                    x={lineTs}
                    stroke={color}
                    strokeWidth={1.2}
                    strokeDasharray="4 3"
                    label={<EventLabel name={ev.name} color={color} />}
                  />
                </React.Fragment>
              );
            })}

            <Line
              dataKey="price"
              dot={false}
              stroke="#1d4ed8"
              strokeWidth={1.5}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
          {events.map((ev, i) => {
            const color = EVENT_COLORS[i % EVENT_COLORS.length];
            return (
              <span key={ev.date} className="flex items-center gap-1.5 text-xs">
                <span
                  className="inline-block w-3 border-t-2"
                  style={{ borderColor: color, borderStyle: "dashed" }}
                />
                <span style={{ color }}>{ev.name}</span>
                <span className="text-[var(--muted)]">
                  ({(ev.startDate || ev.date).slice(0, 7)} a {(ev.endDate || ev.date).slice(0, 7)})
                </span>
              </span>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
